import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, TrendingDown, Server, User, Banknote, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContextMySQL';
import usePrices from '../hooks/usePrices';
import useSeo from '../hooks/useSeo';
import { findServer, groupServersByGame, formatMad } from '../data/servers';
import { createOrderOnServer, saveOrderLocally } from './order/orderFlow';
import {
  THEMES, Field, SelectDropdown, CategoryPicker, QuantityStepper,
  SummaryCard, ErrorBanner, SuccessScreen,
} from './order/orderUi';

const theme = THEMES.sell;

const BANKS = [
  'CIH Bank',
  'Attijariwafa Bank',
  'BMCE Bank of Africa',
  'Banque Populaire',
  'Crédit du Maroc',
  'Société Générale',
  'Barid Bank',
  'Autre',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s().-]{5,19}$/;
const RIB_RE = /^[0-9A-Za-z][0-9A-Za-z\s-]{5,39}$/;
const clampQuantity = (v) => Math.min(50000, Math.max(1, Number.isFinite(v) ? Math.floor(v) : 0));

export default function SellForm() {
  const { user, isAuthenticated, addOrder } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { servers } = usePrices();
  const serverData = groupServersByGame(servers);
  const categories = Object.keys(serverData);
  const initialServer = findServer(searchParams.get('server'), servers)
    || findServer('draconiros') || servers[0] || { game: 'DOFUS 3', name: '' };

  useSeo({
    title: initialServer && searchParams.get('server')
      ? `Vendre des Kamas ${initialServer.name} au Maroc | AV Kamas`
      : 'Vendre des Kamas Dofus au Maroc – Payé en DH | AV Kamas',
    description: `Vente Kamas Maroc : vendez vos Kamas ${initialServer?.name || 'Dofus'} et sur les 20 serveurs Dofus 3, Rétro et Touch, payés en dirhams par virement.`,
    path: '/sell',
  });

  const [formData, setFormData] = useState({
    category: initialServer.game,
    server: initialServer.name,
    quantity: 10,
    characterName: '',
    bankName: 'CIH Bank',
    accountHolder: user?.name || '',
    ribNumber: '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const submittedRef = useRef(false);

  // Pré-remplit les champs quand la session se charge après le premier rendu
  useEffect(() => {
    if (!user) return;
    setFormData(prev => ({
      ...prev,
      accountHolder: prev.accountHolder || user.name || '',
      email: prev.email || user.email || '',
      phone: prev.phone || user.phone || '',
    }));
  }, [user]);

  // Un serveur ajouté depuis l'admin peut arriver après le premier rendu
  useEffect(() => {
    const wanted = findServer(searchParams.get('server'), servers);
    if (wanted) setFormData(prev => prev.server === wanted.name && prev.category === wanted.game
      ? prev
      : { ...prev, category: wanted.game, server: wanted.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servers, searchParams]);

  const selectCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      category: cat,
      server: serverData[cat]?.[prev.server] ? prev.server : Object.keys(serverData[cat] || {})[0] || '',
    }));
  };

  const setField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
    setError('');
  };

  const handleChange = (e) => setField(e.target.name, e.target.value);

  const quantity = clampQuantity(Number(formData.quantity));
  const currentPrice = serverData[formData.category]?.[formData.server]?.sell || 0;
  const totalPrice = currentPrice * quantity;

  const validate = () => {
    const errs = {};
    if (!formData.characterName.trim()) errs.characterName = 'Nom du personnage requis';
    if (!formData.accountHolder.trim()) errs.accountHolder = 'Nom du titulaire requis';
    if (!formData.ribNumber.trim()) errs.ribNumber = 'RIB / numéro de compte requis';
    else if (!RIB_RE.test(formData.ribNumber.trim())) errs.ribNumber = 'RIB invalide (chiffres attendus)';
    if (!formData.email.trim()) errs.email = 'Email requis';
    else if (!EMAIL_RE.test(formData.email.trim())) errs.email = 'Email invalide';
    if (formData.phone.trim() && !PHONE_RE.test(formData.phone.trim())) errs.phone = 'Numéro invalide';
    if (!Number(formData.quantity) || quantity < 1) errs.quantity = 'Quantité minimum : 1M';
    if (!formData.server) errs.server = 'Choisissez un serveur';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittedRef.current) return;

    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError('Veuillez corriger les champs indiqués.');
      const first = document.querySelector('[aria-invalid="true"]');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    submittedRef.current = true;
    setLoading(true);
    setError('');

    const orderData = {
      id: `ORD-${Date.now()}`,
      type: 'sell',
      server: formData.server,
      category: formData.category,
      quantity,
      character: formData.characterName.trim(),
      bankName: formData.bankName,
      accountHolder: formData.accountHolder.trim(),
      accountNumber: formData.ribNumber.trim(),
      pricePerM: currentPrice,
      totalPrice,
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      userId: user?.id || 'guest',
      timestamp: new Date().toISOString(),
    };

    const dbOrderData = {
      id: orderData.id,
      offer_id: null,
      type: 'sell',
      status: 'pending',
      server: formData.server,
      category: formData.category,
      amount: quantity,
      price: currentPrice,
      total_price: totalPrice,
      payment_method: formData.bankName,
      terms: `Titulaire: ${orderData.accountHolder}, RIB: ${orderData.accountNumber}, Banque: ${orderData.bankName}`,
      advertiser_id: 1,
      advertiser_name: 'AVKAMAS Site',
      trader_id: user?.id || 1,
      trader_name: orderData.email,
      email: orderData.email,
      phone: orderData.phone,
      character: orderData.character,
    };

    try {
      const result = await createOrderOnServer(dbOrderData);
      if (result.orderToken) orderData.orderToken = result.orderToken;
      saveOrderLocally(orderData, dbOrderData);

      if (isAuthenticated && user) {
        addOrder({ ...orderData, status: 'pending_payment', date: new Date().toISOString() });
      }

      setSuccess(true);
      setTimeout(() => navigate('/customer-service', { state: { orderData } }), 2000);
    } catch (err) {
      // Même hors-ligne, la commande reste enregistrée localement et le chat s'ouvre
      saveOrderLocally(orderData, dbOrderData);
      setSuccess(true);
      setTimeout(() => navigate('/customer-service', { state: { orderData } }), 2000);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <SuccessScreen
        title="Demande de vente enregistrée !"
        lines={[
          'Votre demande a bien été transmise à notre équipe.',
          'Le support vous confirme la transaction dans le chat, puis le virement part.',
          'Redirection vers le service client…',
        ]}
      />
    );
  }

  const serverOptions = Object.keys(serverData[formData.category] || {}).map(name => ({
    value: name,
    price: serverData[formData.category][name]?.sell,
  }));

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-3">
          <ArrowLeft size={16} />
          Retour
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-5 sm:mb-8">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 border rounded-full mb-3 ${theme.pill}`}>
              <TrendingDown size={16} />
              <span className="text-xs sm:text-sm font-medium">Vendre des Kamas</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1.5">Vendre vos Kamas</h1>
            <p className="text-sm sm:text-base text-gray-400">
              Indiquez vos Kamas et votre RIB — le virement est envoyé après vérification de la transaction.
            </p>
          </div>

          <ErrorBanner message={error} />

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Formulaire */}
            <motion.div
              className="bg-gray-900/80 border border-gray-800 rounded-2xl p-4 sm:p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
                <CategoryPicker
                  categories={categories}
                  value={formData.category}
                  onChange={selectCategory}
                  theme={theme}
                />

                <SelectDropdown
                  label="Serveur"
                  icon={Server}
                  theme={theme}
                  value={formData.server}
                  options={serverOptions}
                  onSelect={(v) => setField('server', v)}
                  renderOption={(opt) => (
                    <span className="flex justify-between items-center gap-2">
                      <span>{opt.value}</span>
                      <span className={`${theme.icon} text-sm whitespace-nowrap`}>{formatMad(opt.price)}/M</span>
                    </span>
                  )}
                />
                {fieldErrors.server && <p role="alert" className="text-xs text-red-400 -mt-3">{fieldErrors.server}</p>}

                <QuantityStepper
                  value={formData.quantity}
                  onChange={(v) => setField('quantity', typeof v === 'number' ? String(clampQuantity((Number(formData.quantity) || 0) + v)) : v)}
                  error={fieldErrors.quantity}
                />

                <Field
                  label="Nom du personnage"
                  name="characterName"
                  required
                  icon={User}
                  theme={theme}
                  value={formData.characterName}
                  onChange={handleChange}
                  error={fieldErrors.characterName}
                  placeholder="Nom exact du perso en jeu"
                  maxLength={80}
                  autoComplete="off"
                />

                <SelectDropdown
                  label="Banque"
                  icon={Banknote}
                  theme={theme}
                  value={formData.bankName}
                  options={BANKS.map(b => ({ value: b }))}
                  onSelect={(v) => setField('bankName', v)}
                />

                <Field
                  label="Nom du titulaire du compte"
                  name="accountHolder"
                  required
                  theme={theme}
                  value={formData.accountHolder}
                  onChange={handleChange}
                  error={fieldErrors.accountHolder}
                  placeholder="Nom complet sur le compte"
                  maxLength={120}
                  autoComplete="name"
                />

                <Field
                  label="RIB / Numéro de compte"
                  name="ribNumber"
                  required
                  theme={theme}
                  value={formData.ribNumber}
                  onChange={handleChange}
                  error={fieldErrors.ribNumber}
                  hint="Vos coordonnées bancaires ne sont transmises qu'à notre équipe."
                  placeholder="XXXXXXXXXXXXXXXXXXXXXXXX"
                  maxLength={40}
                  autoComplete="off"
                  inputMode="numeric"
                />

                <Field
                  label="Email"
                  name="email"
                  type="email"
                  required
                  theme={theme}
                  value={formData.email}
                  onChange={handleChange}
                  error={fieldErrors.email}
                  placeholder="votre@email.com"
                  maxLength={160}
                  autoComplete="email"
                />

                <Field
                  label="Téléphone (optionnel)"
                  name="phone"
                  type="tel"
                  theme={theme}
                  value={formData.phone}
                  onChange={handleChange}
                  error={fieldErrors.phone}
                  placeholder="+212 6XX XX XX XX"
                  maxLength={24}
                  autoComplete="tel"
                />

                {/* Estimation compacte, visible sur mobile avant le bouton */}
                <div className={`md:hidden flex items-center justify-between bg-gradient-to-r ${theme.card} border rounded-xl px-4 py-3`}>
                  <span className="text-sm text-gray-300">Vous recevez : {formData.server} · {quantity}M</span>
                  <span className={`text-xl font-bold ${theme.price}`}>{formatMad(totalPrice)}</span>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className={`w-full ${theme.button} font-bold py-3 sm:py-4 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 text-sm sm:text-base`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <motion.div
                      className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : (
                    <>
                      <TrendingDown size={20} />
                      Envoyer · {formatMad(totalPrice)}
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Colonne estimation : sticky en entier pour que rien ne soit masqué au défilement */}
            <motion.div
              className="space-y-4 md:sticky md:top-24 md:self-start"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <SummaryCard
                theme={theme}
                title="Estimation"
                icon={Wallet}
                server={formData.server}
                quantity={quantity}
                pricePerM={currentPrice}
                total={totalPrice}
                totalLabel="Vous recevez"
                points={['Virement après vérification', 'Taux alignés sur le marché', 'Support disponible']}
                extra={
                  <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                    <p className="text-sm text-yellow-400"><strong>Prochaines étapes :</strong></p>
                    <ol className="list-decimal list-inside mt-2 space-y-1 text-sm text-gray-400">
                      <li>Le support vous indique le personnage destinataire dans le chat</li>
                      <li>Vous envoyez vos Kamas en jeu</li>
                      <li>Le virement part après vérification</li>
                    </ol>
                  </div>
                }
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
